import type { Coordinates } from 'shared/store/store-locator'
import { setUserPosition } from 'shared/store/store-locator'
import { ALL_ZOOM, CENTER, CURRENT_MAP_VIEW, DETAILED_ZOOM } from './MapWoosmap'
import type { Restaurant } from 'shared/types'
import { useEffect } from 'react'
import { debounce } from 'throttle-debounce'
import { useDispatch, useSelector } from 'react-redux'
import ExecutionEnvironment from 'exenv'
import {
    displayGeocodedAddressMarker,
    displayUserPositionMarker
} from './displayMarkers'
import type { RootState } from 'shared/store/reducers'
import { markersBase64 } from './markersBase64'

const woosmapMapOptions = (center: ?Coordinates, zoom: number) => {
    return {
        center: center || CENTER,
        zoom: zoom || ALL_ZOOM
    }
}

//$FlowFixMe
type initMapProps = Omit<useInitMapProps, 'isMapLoaded', 'setMap'> & {
    setUserPos: (p: Coordinates) => any // TODO: What's the type of dispatch(setUserPosition(p))
}

const initMap = ({
    currentView,
    geocodedAddress,
    mapContainerRef,
    restaurant,
    onDeselectRestaurant,
    onStoreSelected,
    setUserPos,
    setZoomAndBbox
}: initMapProps) => {
    const initCenter = !!restaurant
        ? {
              lat: restaurant.coordinates.lat,
              lng: restaurant.coordinates.lng
          }
        : CENTER
    const map = new window.woosmap.map.Map(
        mapContainerRef.current,
        woosmapMapOptions(initCenter, ALL_ZOOM)
    )

    ExecutionEnvironment.canUseDOM &&
        navigator.geolocation.getCurrentPosition((p: Position) => {
            const coords = {
                lat: p.coords.latitude,
                lng: p.coords.longitude
            }
            setUserPos(coords)
            displayUserPositionMarker(map, coords)
            if (currentView !== CURRENT_MAP_VIEW.RESTAURANT) {
                map.setCenter(coords)
                map.setZoom(DETAILED_ZOOM)
            }
        })

    const debouncedSetZoomAndBbox = debounce(100, setZoomAndBbox)

    switch (currentView) {
        case CURRENT_MAP_VIEW.RESTAURANT:
            if (restaurant) {
                let marker = new window.woosmap.map.Marker({
                    position: initCenter,
                    icon: { url: markersBase64.markerActive }
                })
                marker.setMap(map)
                map.setCenter(initCenter)
                map.setZoom(DETAILED_ZOOM)
            }
            break
        default:
            map.addListener('bounds_changed', () => {
                const mapBounds = map.getBounds()
                const mapBbox = [
                    mapBounds._lngRange.min,
                    mapBounds._latRange.min,
                    mapBounds._lngRange.max,
                    mapBounds._latRange.max
                ]
                debouncedSetZoomAndBbox({
                    zoom: map.getZoom(),
                    bbox: mapBbox
                })
            })
            window.woosmap.map.event.addListener(
                map,
                'store_selected',
                selectedStore => {
                    onStoreSelected(selectedStore)
                }
            )
            window.woosmap.map.event.addListener(map, 'click', () => {
                !!onDeselectRestaurant && onDeselectRestaurant()
            })

            if (geocodedAddress) {
                displayGeocodedAddressMarker(map, geocodedAddress)
                map.setCenter(geocodedAddress)
                map.setZoom(DETAILED_ZOOM)
            }
            break
    }

    return map
}

type useInitMapProps = {
    currentView: string,
    isMapLoaded: boolean,
    mapContainerRef: any,
    restaurant?: Restaurant,
    onDeselectRestaurant?: () => void,
    onStoreSelected: (store: any) => void,
    setMap: (map: any) => void,
    setZoomAndBbox: ({ zoom: number, bbox: [number] }) => void
}

export const useInitMap = ({
    currentView,
    isMapLoaded,
    mapContainerRef,
    restaurant,
    onDeselectRestaurant,
    onStoreSelected,
    setMap,
    setZoomAndBbox
}: useInitMapProps) => {
    const dispatch = useDispatch()
    const { geocodedAddress } = useSelector(
        (state: RootState) => state.storeLocatorPageState
    )

    const setUserPos = (p: Coordinates) => dispatch(setUserPosition(p))

    useEffect(() => {
        if (isMapLoaded) {
            setMap(
                initMap({
                    currentView,
                    geocodedAddress,
                    mapContainerRef,
                    restaurant,
                    onDeselectRestaurant,
                    onStoreSelected,
                    setUserPos,
                    setZoomAndBbox
                })
            )
        }
    }, [isMapLoaded, restaurant])
}
